import { BaseMap, Filtro } from "../../../../../interfaces/Indicator";
import { FilterMap, GraphType, MAP_GRAPH_TYPES } from "./FilterOfGraphs";

export const isMapGraph = (type: GraphType): boolean => MAP_GRAPH_TYPES.includes(type as any);

export const isHeatMapGraph = (type: GraphType): boolean => type === 7;

/**
 * Construye los filtros iniciales para gráficos de mapa (tipos 6 y 7).
 * El filtro "Año" respeta el período seleccionado (ej. vía query param)
 * cuando existe entre sus valores disponibles; el resto de filtros toma
 * su primer valor, igual que en los gráficos comunes.
 */
export const buildInitialMapFilters = (
    variables: Filtro[],
    period?: string
): FilterMap => {
    return variables.reduce((acc, variable) => {
        if (variable.name === "Año") {
            const anioValue = period || variable.values[0]?.value;

            if (anioValue) {
                acc["Año"] = [anioValue];
            }
        } else {
            const firstValue = variable.values[0]?.value;
            if (firstValue) {
                acc[variable.name] = [firstValue];
            }
        }

        return acc;
    }, {} as FilterMap);
};

/**
 * Filtra datos de mapa según todos los filtros seleccionados.
 */
export const filterMapData = (data: any[], filters: FilterMap): any[] => {
    return data.filter((item: BaseMap) =>
        Object.entries(filters).every(([filterLabel, filterValues]) => {
            const filterValue = filterValues[0];
            if (!filterValue) return true;

            const itemKey = filterLabel === "Año" ? "anio" : filterLabel;
            return (item as any)[itemKey] === filterValue;
        })
    );
};

/**
 * Parsea el campo "encabezado" (formato "clave.valor" separado por "-")
 * en un objeto plano de pares clave-valor.
 * Ej: "Comuna.Comuna 1-Edades.18 a 25" -> { Comuna: "Comuna 1", Edades: "18 a 25" }
 */
export const parseEncabezado = (encabezado: string): Record<string, string> => {
    return (encabezado || "").split('-').reduce((acc, part) => {
        const [key, value] = part.split('.');
        if (key?.trim()) {
            acc[key.trim()] = value?.trim() ?? '';
        }
        return acc;
    }, {} as Record<string, string>);
};

/**
 * Determina si un "encabezado" (+ su anio) matchea los filtros seleccionados.
 * Un valor "Todos" no filtra esa dimensión.
 */
export const matchesSelectedFilters = (
    criteria: FilterMap,
    encabezado: string,
    anio?: string
): boolean => {
    return Object.entries(criteria).every(([key, values]) => {
        const lowerKey = key.toLowerCase().trim();
        const filterValues = (values || []).map((v) => v.toLowerCase());

        if (filterValues.includes('todos')) return true;

        if (key === "Año") {
            return filterValues.includes(String(anio).toLowerCase());
        }

        const matchingPart = encabezado
            ?.toLowerCase()
            .split('-')
            .find((part) => part.split('.')[0].trim() === lowerKey);

        if (!matchingPart) return false;

        const extractedValue = matchingPart.split('.')[1]?.trim();
        return filterValues.includes(extractedValue);
    });
};

/**
 * Filtra y agrupa los datos de mapas de calor por comuna, sumando "valor".
 * A diferencia de filterMapData, acá los filtros que no vienen como campo
 * plano del dato (ej. "Edades") viven dentro de "encabezado" (ver matchesSelectedFilters).
 * Un valor "Todos" no filtra esa dimensión, por eso puede haber varias filas
 * por comuna que hay que sumar.
 */
export const filterAndGroupMapDataByComuna = (
    criteria: FilterMap,
    data: any[]
): any[] => {
    const filteredData = data.filter((item) =>
        matchesSelectedFilters(criteria, item.encabezado, item.anio)
    );

    const groupedByComuna: Record<string, number> = {};

    filteredData.forEach((item) => {
        const comuna = item.comuna;
        const valor = parseFloat(item.valor);
        if (!comuna || isNaN(valor)) return;

        groupedByComuna[comuna] = (groupedByComuna[comuna] || 0) + valor;
    });

    return Object.entries(groupedByComuna).map(([comuna, valor]) => ({
        comuna,
        valor: valor.toString(),
        anio: criteria["Año"]?.[0] ?? "",
    }));
};

/**
 * Construye los filtros iniciales basándose en las variables disponibles
 * y las variables de análisis configuradas
 */
export const buildInitialFilters = (
    variables: Filtro[],
    variable_analisis: string[]
): FilterMap => {
    return variables.reduce((acc, variable) => {
        if (variable_analisis.includes(variable.name)) {
            acc[variable.name] = variable.values.map((v) => v.value);
        } else {
            const firstValue = variable.values[0]?.value;
            if (firstValue) {
                acc[variable.name] = [firstValue];
            }
        }
        return acc;
    }, {} as FilterMap);
};

/**
 * Verifica si dos conjuntos de filtros son idénticos
 */
export const areFiltersEqual = (
    filters1: FilterMap,
    filters2: FilterMap
): boolean => {
    const keys1 = Object.keys(filters1);
    const keys2 = Object.keys(filters2);
    if (keys1.length !== keys2.length) return false;

    return keys1.every((key) => {
        const values1 = filters1[key] || [];
        const values2 = filters2[key] || [];

        if (values1.length !== values2.length) return false;
        const set2 = new Set(values2);
        return values1.every((val) => set2.has(val));
    });
};